import React, { useEffect, useState } from 'react';

import clsx from 'clsx';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import useChangeValue from 'apps/web_forms/hooks/useChangeValue';
import useResourceUpdateHighlight from 'apps/web_forms/hooks/useResourceUpdateHighlight';
import { selectFieldErrorById } from 'apps/web_forms/hooks/useGlobalSelector';
import useRules from 'apps/web_forms/hooks/useRules';
import { setCurrentSelectedMultimediaInput } from 'apps/web_forms/slices/multimediaSlice';
import customPropTypes from 'apps/web_forms/utils/customPropTypes';

const PhoneNumberInput = ({ element, formContext, onChangeValue, toggleActiveValue, showContent }) => {
  const {
    title,
    identifier,
    hint,
    hidden, 
    validate_msg: validateMsg,
    disable_formatting: disableFormatting,
  } = element;
  const { register } = formContext;

  const { changeValue, currentValue } = useChangeValue(
    element,
    onChangeValue,
    formContext
  );

  const errorMessage = useSelector((state) => selectFieldErrorById(state, identifier));
  
  const { hasResourceUpdateHighlight, removeResourceUpdateHighlight } =
    useResourceUpdateHighlight({ fieldIdentifier: identifier });

  const dispatch = useDispatch();
  const [{ isActive, isRequired, isElementValid, isReadOnly, initialAnswer }] =
    useRules(element, formContext);

  useEffect(() => {
    const usPhoneNumberRegex =
      /^(\+\d{1,2}\s?)?1?-?\.?\s?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}$/i;
    const validationObj = {
      valid: () => isElementValid || validateMsg || false,
    };

    if (!disableFormatting) {
      validationObj.phoneFormating = (value) => {
        if (value === undefined) return true;
        return (
          value.match(usPhoneNumberRegex) ||
          value === '' ||
          'Please use a US phone number format'
        );
      };
    }

    register(identifier, {
      validate: {
        ...validationObj,
      },
      required: isRequired ? 'This field is required' : false,
    });
  }, [isElementValid, isRequired]);

  useEffect(() => {
    changeValue(initialAnswer);
  }, [initialAnswer]);

  const onChange = (value) => {
    changeValue(value, true);
  };

  useEffect(() => {
    toggleActiveValue(identifier, isActive);
  }, [isActive]);

  if (!isActive || hidden || !showContent) return null;

  return (
    <div className='react-form-control'>
      <label htmlFor={identifier}>{title}</label>
      <input
        type='text'
        id={identifier}
        // value={currentValue || ''}
        defaultValue={currentValue || ''}
        name={identifier}
        placeholder='+1(855)257-9650'
        title='E.g.: +1(855)257-9650'
        onClick={() => dispatch(setCurrentSelectedMultimediaInput({}))}
        onFocus={removeResourceUpdateHighlight}
        className={clsx(
          errorMessage !== undefined && 'invalid',
          hasResourceUpdateHighlight && 'updateHighlight',
          isReadOnly && 'read-only'
        )}
        readOnly={isReadOnly}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <p className='element-hint'>{hint}</p>}
      {errorMessage !== undefined && (
        <ErrorMessage id={`${identifier}_errmsg`}>{errorMessage}</ErrorMessage>
      )}
    </div>
  );
};

PhoneNumberInput.propTypes = {
  element: PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    initialAnswer: PropTypes.string,
    hint: PropTypes.string,
    hidden: PropTypes.bool,
    validate_msg: PropTypes.string,
    disable_formatting: PropTypes.bool,
  }).isRequired,
  formContext: customPropTypes.formContext.isRequired,
  onChangeValue: PropTypes.func.isRequired,
};

export default React.memo(PhoneNumberInput);
